import { useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiClientError } from '../api/client';
import {
  assignableRoles,
  changeEmployeeRole,
  inviteEmployee,
  listCompanies,
  listEmployees,
  destroyEmployee,
  employeeImpact,
  reinstateEmployee,
  resignEmployee,
  type Employee,
} from '../api/directory';
import { useAuth } from '../auth/AuthProvider';
import { ClickableRow, RowTitle } from '../components/ClickableRow';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toasts';
import { Icon } from '../lib/icons';
import { EMPLOYEE_BADGE, badgeFor } from '../lib/statusStyles';

const PAGE_SIZE = 100;

/**
 * FEAT-012 — the employee directory.
 *
 * Cross-company roles get a company filter; everyone else is silently pinned to
 * their own employer by the server (§4.4), so the control is simply not
 * rendered for them rather than shown and ignored.
 */
export function EmployeesPage(): ReactElement {
  const { t } = useTranslation();
  const { can, user } = useAuth();
  const { notify } = useToast();
  const queryClient = useQueryClient();

  const isCrossCompany = user?.is_cross_company ?? false;

  const [employer, setEmployer] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [roleTarget, setRoleTarget] = useState<Employee | null>(null);
  const [destroying, setDestroying] = useState<Employee | null>(null);
  const [viewing, setViewing] = useState<Employee | null>(null);

  const query = useQuery({
    queryKey: ['employees', employer],
    queryFn: () =>
      listEmployees({ page: 1, pageSize: PAGE_SIZE, ...(employer ? { employer } : {}) }),
  });

  // Only cross-company roles can read the registry, so only they get the filter.
  const companies = useQuery({
    queryKey: ['companies', ''],
    queryFn: () => listCompanies({ page: 1, pageSize: 100 }),
    enabled: isCrossCompany && can('Companies.Manage'),
  });

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: ['employees'] });
  }

  const statusChange = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'resign' | 'reinstate' }) =>
      action === 'resign' ? resignEmployee(id) : reinstateEmployee(id),
    onSuccess: (employee) => {
      refresh();
      notify(
        employee.status === 'resigned'
          ? t('employee_resigned', { name: employee.name })
          : t('employee_reinstated', { name: employee.name }),
      );
    },
    onError: (error) => {
      if (error instanceof ApiClientError) notify(error.message, 'bad');
    },
  });

  return (
    <>
      <div className="card">
        <div className="card-head">
          <h1>{t('employees')}</h1>

          <div className="ch-tools">
            {isCrossCompany && companies.data ? (
              <>
                <label className="sr-only" htmlFor="employer-filter">
                  {t('col_company')}
                </label>
                <select
                  id="employer-filter"
                  value={employer}
                  onChange={(e) => setEmployer(e.target.value)}
                >
                  <option value="">{t('all_companies')}</option>
                  {companies.data.items.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </>
            ) : null}

            {can('Employees.Manage') ? (
              <button type="button" className="btn primary" onClick={() => setIsInviting(true)}>
                <Icon name="plus" />
                {t('invite_employee')}
              </button>
            ) : null}
          </div>
        </div>

        {query.isPending ? (
          <div className="empty" role="status" aria-live="polite">
            {t('loading')}
          </div>
        ) : query.error ? (
          <div className="empty" role="alert">
            {query.error instanceof ApiClientError ? query.error.message : t('login_network_error')}
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">{t('col_name')}</th>
                  <th scope="col">{t('email')}</th>
                  <th scope="col">{t('col_role')}</th>
                  <th scope="col">{t('col_company')}</th>
                  <th scope="col">{t('col_status')}</th>
                  <th scope="col">
                    <span className="sr-only">{t('col_actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {query.data?.items.map((employee) => (
                  <ClickableRow key={employee.id} onOpen={() => setViewing(employee)}>
                    <td>
                      <RowTitle onOpen={() => setViewing(employee)}>{employee.name}</RowTitle>
                      {employee.is_comm_contact ? (
                        <span className="chip">{t('comm_contact')}</span>
                      ) : null}
                    </td>
                    <td>{employee.email}</td>
                    <td>{t(`role_${employee.role}`)}</td>
                    <td>{employee.employer_name}</td>
                    <td>
                      <span className={badgeFor(EMPLOYEE_BADGE, employee.status)}>
                        {t(`emp_${employee.status}`)}
                      </span>
                    </td>
                    <td>
                      <div className="row-actions">
                        {/*
                        The system chief is immutable to everyone, including
                        itself (§1.5), so it offers no actions at all. The server
                        refuses them regardless.
                      */}
                        {employee.is_system_chief ? (
                          <span className="chip">{t('system_chief')}</span>
                        ) : (
                          <>
                            {can('Employees.RoleChange') && employee.id !== user?.id ? (
                              <button
                                type="button"
                                className="btn sm"
                                onClick={() => setRoleTarget(employee)}
                                aria-label={`${t('change_role')} — ${employee.name}`}
                              >
                                {t('change_role')}
                              </button>
                            ) : null}

                            {can('Employees.Remove') && employee.id !== user?.id ? (
                              <button
                                type="button"
                                className="btn sm"
                                onClick={() =>
                                  statusChange.mutate({
                                    id: employee.id,
                                    action: employee.status === 'resigned' ? 'reinstate' : 'resign',
                                  })
                                }
                                aria-label={`${
                                  employee.status === 'resigned' ? t('reinstate') : t('resign')
                                } — ${employee.name}`}
                              >
                                {employee.status === 'resigned' ? t('reinstate') : t('resign')}
                              </button>
                            ) : null}

                            {/*
                              Super Admin only, and the server enforces it —
                              Reception holds `Employees.Remove` for the
                              reversible action and must not inherit this one.
                              Styled `danger` and placed last, because it is the
                              only control on this screen that destroys anything.
                            */}
                            {user?.role === 'superadmin' && employee.id !== user?.id ? (
                              <button
                                type="button"
                                className="btn sm danger"
                                onClick={() => setDestroying(employee)}
                                aria-label={`${t('emp_delete_forever')} — ${employee.name}`}
                              >
                                {t('emp_delete_forever')}
                              </button>
                            ) : null}
                          </>
                        )}
                      </div>
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewing ? (
        <EmployeeDetailDialog employee={viewing} onClose={() => setViewing(null)} />
      ) : null}

      {isInviting ? (
        <InviteDialog
          defaultEmployer={user?.employer_id ?? 'boud'}
          companies={companies.data?.items ?? []}
          onClose={() => setIsInviting(false)}
          onInvited={(name) => {
            setIsInviting(false);
            refresh();
            notify(t('employee_invited', { name }));
          }}
        />
      ) : null}

      {roleTarget ? (
        <RoleDialog
          employee={roleTarget}
          onClose={() => setRoleTarget(null)}
          onChanged={(name) => {
            setRoleTarget(null);
            refresh();
            notify(t('role_changed', { name }));
          }}
        />
      ) : null}

      {destroying ? (
        <DestroyDialog
          employee={destroying}
          onClose={() => setDestroying(null)}
          onDeleted={(name) => {
            setDestroying(null);
            refresh();
            notify(t('emp_deleted', { name }));
          }}
        />
      ) : null}
    </>
  );
}

/**
 * REQ-073 — the confirmation for a permanent deletion.
 *
 * Its own dialog rather than a `window.confirm`, for one reason: it has to say
 * *what will be destroyed* before it is destroyed. This is the only action in
 * the platform that cannot be undone, and "are you sure?" is not informed
 * consent when the answer depends on whether the person organised four meetings
 * other people are attending.
 *
 * It also offers the reversible alternative, because most of the time that is
 * what somebody actually wants.
 */
function DestroyDialog({
  employee,
  onClose,
  onDeleted,
}: {
  employee: Employee;
  onClose: () => void;
  onDeleted: (name: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();

  const impact = useQuery({
    queryKey: ['employee-impact', employee.id],
    queryFn: () => employeeImpact(employee.id),
  });

  const destroy = useMutation({
    mutationFn: () => destroyEmployee(employee.id),
    onSuccess: () => onDeleted(employee.name),
    onError: (error) =>
      notify(error instanceof ApiClientError ? error.message : t('login_network_error'), 'bad'),
  });

  const counts = impact.data;

  return (
    <Modal
      title={t('emp_delete_title', { name: employee.name })}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="button"
            className="btn danger"
            /*
             * Held back until the counts have loaded. Confirming a destruction
             * whose scale you have not been shown is exactly the thing this
             * dialog exists to prevent.
             */
            disabled={destroy.isPending || impact.isPending}
            onClick={() => destroy.mutate()}
          >
            {destroy.isPending ? t('saving') : t('emp_delete_confirm')}
          </button>
        </>
      }
    >
      <div>
        <p>{t('emp_delete_lead')}</p>

        {impact.isPending ? (
          <p role="status">{t('loading')}</p>
        ) : counts && counts.bookings + counts.tickets === 0 ? (
          <p>{t('emp_delete_nothing')}</p>
        ) : counts ? (
          <>
            {/*
              Two sentences rather than one, because a single string cannot
              pluralise two different numbers — "1 room bookings and 1 support
              requests" is what that costs, in both languages.
            */}
            <p>
              <strong>
                {counts.bookings > 0 ? t('emp_delete_bookings', { count: counts.bookings }) : null}{' '}
                {counts.tickets > 0 ? t('emp_delete_tickets', { count: counts.tickets }) : null}
              </strong>
            </p>
            {counts.upcoming_bookings > 0 ? (
              <p>{t('emp_delete_upcoming', { count: counts.upcoming_bookings })}</p>
            ) : null}
          </>
        ) : null}

        <p className="hint">{t('emp_delete_alt')}</p>
      </div>
    </Modal>
  );
}

function InviteDialog({
  defaultEmployer,
  companies,
  onClose,
  onInvited,
}: {
  defaultEmployer: string;
  companies: { id: string; name: string }[];
  onClose: () => void;
  onInvited: (name: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();

  const [form, setForm] = useState({
    name: '',
    email: '',
    employer_id: defaultEmployer,
    phone: '',
    fingerprint_id: '',
    office: '',
    employee_no: '',
  });
  const [role, setRole] = useState('employee');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Recomputed server-side on every save; this only renders a correct list.
  const roles = useQuery({
    queryKey: ['assignable-roles', form.employer_id],
    queryFn: () => assignableRoles(form.employer_id),
  });

  const mutation = useMutation({
    mutationFn: () => inviteEmployee({ ...form, role }),
    onSuccess: (employee) => onInvited(employee.name),
    onError: (error) => {
      if (error instanceof ApiClientError) {
        setFieldErrors(
          error.code === 'duplicate_email' ? { email: error.message } : error.fieldErrors,
        );
        notify(error.message, 'bad');
      }
    },
  });

  function set(key: keyof typeof form) {
    return (value: string) => setForm((current) => ({ ...current, [key]: value }));
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    setFieldErrors({});
    mutation.mutate();
  }

  return (
    <Modal
      title={t('invite_employee')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="submit"
            form="invite-form"
            className="btn primary"
            disabled={mutation.isPending}
          >
            {mutation.isPending ? t('saving') : t('invite_employee')}
          </button>
        </>
      }
    >
      <form id="invite-form" onSubmit={submit} noValidate>
        <TextField
          id="emp-name"
          label={t('col_name')}
          value={form.name}
          onChange={set('name')}
          error={fieldErrors.name}
          required
        />
        <TextField
          id="emp-email"
          label={t('email')}
          value={form.email}
          onChange={set('email')}
          error={fieldErrors.email}
          type="email"
          required
        />

        {companies.length > 0 ? (
          <div className="field">
            <label htmlFor="emp-employer">{t('col_company')}</label>
            <select
              id="emp-employer"
              value={form.employer_id}
              onChange={(e) => set('employer_id')(e.target.value)}
            >
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="emp-role">{t('col_role')}</label>
          <select id="emp-role" value={role} onChange={(e) => setRole(e.target.value)}>
            {(roles.data?.roles ?? ['employee']).map((value) => (
              <option key={value} value={value}>
                {t(`role_${value}`)}
              </option>
            ))}
          </select>
        </div>

        <TextField
          id="emp-phone"
          label={t('col_phone')}
          value={form.phone}
          onChange={set('phone')}
          error={fieldErrors.phone}
          required
        />
        <TextField
          id="emp-fingerprint"
          label={t('col_fingerprint')}
          value={form.fingerprint_id}
          onChange={set('fingerprint_id')}
          error={fieldErrors.fingerprint_id}
          required
        />
        <TextField
          id="emp-office"
          label={t('col_office')}
          optional
          value={form.office}
          onChange={set('office')}
          error={fieldErrors.office}
        />
        <TextField
          id="emp-number"
          label={t('col_employee_no')}
          optional
          value={form.employee_no}
          onChange={set('employee_no')}
          error={fieldErrors.employee_no}
        />

        {/*
          Until the Workspace SMTP relay is wired in Phase 6, the invitation
          link goes to the API log rather than to an inbox. Saying so here is
          better than letting an administrator believe mail was sent.
        */}
        <p className="field-note">{t('invitation_console_notice')}</p>
      </form>
    </Modal>
  );
}

function RoleDialog({
  employee,
  onClose,
  onChanged,
}: {
  employee: Employee;
  onClose: () => void;
  onChanged: (name: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();
  const [role, setRole] = useState(employee.role);

  const roles = useQuery({
    queryKey: ['assignable-roles', employee.employer_id],
    queryFn: () => assignableRoles(employee.employer_id),
  });

  const mutation = useMutation({
    mutationFn: () => changeEmployeeRole(employee.id, role),
    onSuccess: () => onChanged(employee.name),
    onError: (error) => {
      if (error instanceof ApiClientError) notify(error.message, 'bad');
    },
  });

  return (
    <Modal
      title={`${t('change_role')} — ${employee.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || role === employee.role}
          >
            {mutation.isPending ? t('saving') : t('save')}
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="role-select">{t('col_role')}</label>
        <select id="role-select" value={role} onChange={(e) => setRole(e.target.value)}>
          {(roles.data?.roles ?? [employee.role]).map((value) => (
            <option key={value} value={value}>
              {t(`role_${value}`)}
            </option>
          ))}
        </select>
      </div>

      {/* REQ-070 — this applies to their very next request, not at next login. */}
      <p className="field-note">{t('role_change_immediate')}</p>
    </Modal>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
  error,
  required,
  optional,
  type = 'text',
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  /** Marked with the prototype's `.opt` span, so what may be skipped is visible. */
  optional?: boolean;
  type?: string;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {optional ? <span className="opt"> {t('optional')}</span> : null}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={error ? 'bad' : ''}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        required={required}
      />
      {error ? (
        <div id={`${id}-error`} className="field-error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A read-only employee record, matching the prototype's `detailEmp`.
 *
 * The row opens this rather than jumping straight into "change role": opening a
 * record and changing it are different intentions, and a row-wide click should
 * never be the one that does the changing.
 */
function EmployeeDetailDialog({
  employee,
  onClose,
}: {
  employee: Employee;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <Modal
      title={employee.name}
      onClose={onClose}
      footer={
        <button type="button" className="btn ghost" onClick={onClose}>
          {t('close')}
        </button>
      }
    >
      <dl className="dl">
        <dt>{t('emp_email')}</dt>
        <dd>{employee.email}</dd>

        <dt>{t('emp_company')}</dt>
        <dd>{employee.employer_name}</dd>

        <dt>{t('emp_role')}</dt>
        <dd>
          {t(`role_${employee.role}`)}
          {employee.is_comm_contact ? <span className="chip">{t('comm_contact')}</span> : null}
        </dd>

        {/* Pinned LTR: a phone number reads the same in both languages. */}
        <dt>{t('emp_phone')}</dt>
        <dd dir="ltr">{employee.phone}</dd>

        {employee.employee_no ? (
          <>
            <dt>{t('emp_empno')}</dt>
            <dd dir="ltr">{employee.employee_no}</dd>
          </>
        ) : null}

        <dt>{t('emp_office')}</dt>
        <dd>{employee.office ?? '—'}</dd>

        <dt>{t('status')}</dt>
        <dd>
          <span className={badgeFor(EMPLOYEE_BADGE, employee.status)}>
            {t(`emp_${employee.status}`)}
          </span>
        </dd>
      </dl>
    </Modal>
  );
}
